import React, { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ButtonComp } from '../../../../atoms/Buttons';
import ImageComponent from 'atoms/Image';
import geoLocation from '@kfc-global/react-shared/utils/geoLocation';
import { addNewAddress } from '@kfc-global/react-shared/redux/Actions/OrderservAction';
import { resetStores } from '@kfc-global/react-shared/redux/Actions/StoresAction';
import ModalComponent from '../../../../molecules/Modal';
import ADDRESS_STORE_CONSTANTS from './constant';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { isEmptyObject } from 'common/utilities/utils';
import AuthManager from '@kfc-global/react-shared/auth/AuthManager';
import { IMAGE_PATH } from 'common/ImageConstants/ImagePathUtils';

const { SuggestionItemSearchPin } = IMAGE_PATH;

const renderSuggestions = (data, status, handleSelect) =>
  status?.toUpperCase() === ADDRESS_STORE_CONSTANTS.OK &&
  data.map(suggestion => {
    const {
      place_id,
      structured_formatting: { main_text, secondary_text },
    } = suggestion;
    return (
      <>
        {suggestion.terms.filter(obj => obj.value === ADDRESS_STORE_CONSTANTS.INDIA)?.length > 0 && (
          <li
            key={place_id}
            data-testid='address-suggestion-id'
            className='address-suggestion-text'
            onClick={handleSelect(suggestion)}
          >
            <ImageComponent srcFile={SuggestionItemSearchPin} alt={'googleMapLocation'} className='suggetion-pin' />
            <span className='address-text'>
              <strong>{main_text}</strong> {secondary_text}
            </span>
          </li>
        )}
      </>
    );
  });

/**
 * The element renders the coupons landing modal with close button
 * @param {*} props
 * @props isOpen: Boolean value to determine if modal is open
 * @props handleClose: Handler function for closing the Modal
 * @returns JSX.Element
 */
export const AddressModal = props => {
  const dispatch = useDispatch();
  const { isOpen, modalType, handleClose, storeSearch, tenantId, allStores } = props;
  const { ADD_NEW_ADDRESS, EDIT_DELIVERY_ADDRESS, HIDDEN, DELIVERY_SERVICE, CHANNEL, NOT_SERVICEABLE, INITIAL_STATE } =
    ADDRESS_STORE_CONSTANTS;
  const address = isEmptyObject(props.address) ? props.address : INITIAL_STATE;
  const [addresses, setSaveAddress] = useState(address);
  const [error, setError] = useState(false);
  const geohashprecision = storeSearch?.geohashprecision;
  const boundingBoxSize = storeSearch?.boundingBoxSizeMeters / 1000;

  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: {
        country: 'in',
      },
      /* Define search scope here */
    },
    debounce: 300,
  });

  const suggestionRef = useRef(null);

  useEffect(() => {
    if (modalType) {
      geoLocation.calculateGH({
        lat: parseFloat(addresses?.location?.latitude || addresses?.latitude),
        lon: parseFloat(addresses?.location?.longitude || addresses?.longitude),
        distanceFromCurrentLoc: boundingBoxSize,
        geoHashPrecision: geohashprecision,
      });
    }
    document.querySelector('body').style.overflow = HIDDEN;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  });

  const handleClickOutside = event => {
    if (suggestionRef.current && !suggestionRef.current.contains(event.target)) {
      clearSuggestions();
    }
  };

  const handleInput = e => {
    setValue(e.target.value);
    const addressLines = [addresses?.addressLines[0], e.target.value];
    setSaveAddress({ ...addresses, addressLines: addressLines });
  };

  const handleChange = ({ target }) => {
    const { name, value: Value } = target;
    if (name === 'floor') {
      setSaveAddress({ ...addresses, [name]: Value });
      const addressLines = [Value, addresses?.addressLines[1]];
      setSaveAddress({ ...addresses, addressLines: addressLines });
      return;
    }
    if (target.type !== 'checkbox') {
      setSaveAddress({ ...addresses, [name]: Value });
    } else {
      setSaveAddress({ ...addresses, [name]: Value !== 'true' });
    }
  };

  const getAddressLines = addressVal => {
    let addLine2 = [];
    Object.entries(addressVal).forEach(components => {
      switch (components[0]) {
        case 'route':
        case 'street_address':
        case 'neighborhood':
        case 'sublocality_level_1':
        case 'sublocality_level_2':
        case 'sublocality_level_3':
          addLine2.push(components[1]);
          break;
        // no default
      }
      if (!addLine2.length) {
        if (components[0] === 'sublocality' || components[0] === 'administrative_area_level_2') {
          addLine2.push(components[1]);
        }
      }
    });
    addLine2 = addLine2.join(',');
    return addLine2;
  };

  const setAddressDetails = addressDetails => {
    const addressData = addressDetails.address_components.reduce((seed, { long_name, types }) => {
      types.forEach(t => (seed[t] = long_name));
      return seed;
    }, {});
    const getAddressFormat = getAddressLines(addressData);
    let addressLines = [addressData?.street_number || '', getAddressFormat];
    setValue(getAddressFormat);
    setSaveAddress(prevState => ({
      ...prevState,
      country: addressData?.country,
      addressLines: addressLines,
      city: addressData?.administrative_area_level_2 || addressData?.locality,
      state: addressData?.administrative_area_level_1 || '',
      pinCode: addressData?.postal_code || '',
    }));
    checkFormValidation();
    setTimeout(() => {
      clearSuggestions();
    }, 800);
  };

  const handleSelect =
    ({ description, structured_formatting: { main_text, secondary_text } }) =>
    () => {
      // When user selects a place, we can replace the keyword without request data from API
      // by setting the second parameter to "false"
      // setValue(main_text, false);
      setError(false);
      clearSuggestions();
      dispatch(resetStores());
      // Get latitude and longitude via utility functions
      getGeocode({
        address: description,
        componentRestrictions: {
          country: 'in',
        },
      })
        .then(results => {
          setAddressDetails(results[0]);
          return getLatLng(results[0]);
        })
        .then(data => {
          geoLocation.calculateGH({
            lat: parseFloat(data?.lat),
            lon: parseFloat(data?.lng),
            distanceFromCurrentLoc: boundingBoxSize,
            geoHashPrecision: geohashprecision,
          });
          setSaveAddress(prevState => ({
            ...prevState,
            latitude: data?.lat,
            longitude: data?.lng,
          }));
        })
        .catch(error => {
          console.log('Error: ', error);
        });
    };

  const checkFormValidation = () => {
    return (
      addresses?.addressLines[1].length &&
      addresses?.name?.length &&
      addresses?.city?.length &&
      addresses?.pinCode?.length &&
      addresses?.state?.length
    );
  };

  const filterStores = store => {
    return (
      store?.id &&
      store?.location?.latitude &&
      store?.location?.longitude &&
      store?.availableServicesPerChannel?.filter(
        service => service?.channel === CHANNEL && service?.services?.disposition === DELIVERY_SERVICE,
      )
    );
  };

  const getPayloadData = addresses => {
    let data = {};
    let addressLines = ['', ''];
    addressLines[1] = addresses.addressLines[1];
    addressLines[0] = addresses?.floor || addresses.addressLines[0];
    data = {
      addressId: addresses?.id || '',
      addressName: addresses.name || '',
      city: addresses.city,
      postalCode: addresses.pinCode,
      state: addresses.state,
      country: addresses.country || '',
      isPrimary: addresses.isPrimary || false,
      location: {
        latitude: addresses?.location?.latitude || addresses?.latitude, //15.749100576992188,
        longitude: addresses?.location?.longitude || addresses?.longitude, //75.25417921693628
      },
      addressLines: addressLines,
    };
    return data;
  };

  const handleSubmit = () => {
    const data = getPayloadData(addresses);
    const validStores = allStores?.filter(filterStores);
    const driveTime = storeSearch?.maxDriveTimeMins;
    //Linear Distance
    const sortedStoresList = geoLocation.checkAndProceed({
      stores: validStores,
      disposition: DELIVERY_SERVICE,
      distanceMetrics: storeSearch?.distanceMetrics,
      driveTime,
    });

    if (sortedStoresList?.length) {
      const { phoneNumber, customerId } = AuthManager.getInstance();
      dispatch(
        addNewAddress({
          customerId,
          phoneNumber,
          tenantId,
          data,
        }),
      );
      handleClose();
    } else {
      setError(true);
    }
  };

  const setStatus = () => {
    setValue(value);
  };

  /**
   * Changing of the props sent to modal component based on screen-width
   */
  const modalProps = {
    id: 'AddressModalPopup',
    role: 'dialog',
    modalTitleId: 'AddressModalPopup',
    show: isOpen,
    classNames: 'address-module-modal',
    showCloseBtn: true,
    showBackBtn: false,
    closeButtonHandler: handleClose,
    modalTitle: !modalType ? ADD_NEW_ADDRESS : EDIT_DELIVERY_ADDRESS,
    autoFocus: true,
  };

  const onKeyPressed = e => {
    e.preventDefault();
    return false;
  };

  return (
    <ModalComponent {...modalProps} data-testid='address-modal-landing-page'>
      <div className='address-form-section'>
        <div className='form-container'>
          {error && <div className='error-service-msg'>{NOT_SERVICEABLE}</div>}
          <div className='form-header'>Address</div>
          <form autocomplete='off'>
            <div className='input-container'>
              <input
                required
                type='text'
                data-testid='name-testid'
                name='name'
                value={addresses?.name}
                onChange={handleChange}
              />
              <label>Address Nickname*</label>
            </div>

            <div className='input-container'>
              {/* name='addressLines' value={getAddresLines(addresses?.addressLines)} onChange={handleChange} */}
              <input
                data-testid='search-input-address'
                value={value || addresses?.addressLines[1]}
                onChange={handleInput}
                disabled={!ready}
                name='addressName'
                onFocus={() => setStatus()}
                required
                type='text'
              />
              <label>Address*</label>
              <ul ref={suggestionRef} className='address-list'>
                {renderSuggestions(data, status, handleSelect)}
              </ul>
            </div>

            <div className='d-flex adjust-width flex-dir'>
              <div className='input-container'>
                <input
                  required
                  type='text'
                  name='floor'
                  value={addresses?.floor || addresses?.addressLines[0]}
                  onChange={handleChange}
                />
                <label>Apt / Suite / Floor</label>
              </div>
              <div className='input-container ml5'>
                <input
                  className='p-none'
                  required
                  type='text'
                  name='pinCode'
                  onKeyDown={e => onKeyPressed(e)}
                  onKeyPress={e => onKeyPressed(e)}
                  value={addresses?.pinCode}
                  tabIndex='-1'
                  data-testid='pincode-id'
                  onChange={handleChange}
                />
                <label>Postal Code*</label>
              </div>
            </div>

            <div className='d-flex adjust-width flex-dir'>
              <div className='input-container'>
                <input
                  className='p-none'
                  required
                  type='text'
                  tabIndex='-1'
                  name='city'
                  onKeyDown={e => onKeyPressed(e)}
                  onKeyPress={e => onKeyPressed(e)}
                  value={addresses?.city}
                  data-testid='city-id'
                  onChange={handleChange}
                />
                <label>City / Town*</label>
              </div>
              <div className='input-container ml5'>
                <input
                  className='p-none'
                  required
                  type='text'
                  tabIndex='-1'
                  name='state'
                  onKeyDown={e => onKeyPressed(e)}
                  onKeyPress={e => onKeyPressed(e)}
                  value={addresses?.state}
                  data-testid='state-id'
                  onChange={handleChange}
                />
                <label>State*</label>
              </div>
            </div>
            <div className='default-address-section'>
              <input
                type='checkbox'
                name='isPrimary'
                data-testid='default-testid'
                id='default-address'
                value={addresses?.isPrimary}
                checked={addresses?.isPrimary}
                onChange={handleChange}
              />
              <div className='default-label-text'>
                <label htmlFor='default-address'>Make this my default address</label>
              </div>
            </div>
          </form>
        </div>
      </div>
      <div className='input-container text-center'>
        <ButtonComp
          onClick={handleClose}
          data-testid='close-testid'
          className={`mt-10 button font-bold whiteButton blackBorder`}
        >
          Cancel
        </ButtonComp>
        <ButtonComp
          disabled={!checkFormValidation()}
          className={`mt-10 button font-bold whiteButton blkColor ml5`}
          data-testid='submit-testid'
          onClick={handleSubmit}
        >
          Save
        </ButtonComp>
      </div>
    </ModalComponent>
  );
};

export default AddressModal;
